import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FileText, Loader2 } from "lucide-react";
import { api, errMsg, TYPE_LABELS } from "@/lib/api";
import { Dropzone } from "@/components/Dropzone";
import { Textarea } from "@/components/ui/textarea";

const Working = ({ title, note }) => (
  <div className="border border-[var(--rule)] bg-[var(--card)] px-8 py-20 text-center" data-testid="working-state">
    <div className="relative mx-auto w-14 h-[72px] border border-[var(--ink)] bg-[var(--paper)] overflow-hidden mb-8">
      <div className="scan-line absolute left-0 right-0 top-1 h-px bg-[var(--ink)]" />
    </div>
    <h3 className="font-serif-d text-3xl mb-2">{title}</h3>
    <p className="text-sm text-[var(--ink-2)]">{note}</p>
  </div>
);

const Confirm = ({ doc, type, setType, context, setContext, playbooks, onRun }) => {
  const needs = playbooks.find((p) => p.slug === type)?.needs_context;
  return (
    <div className="border border-[var(--rule)] bg-[var(--card)] p-8 md:p-10 rise" data-testid="confirm-type-panel">
      <div className="flex items-start gap-4 pb-8 border-b border-[var(--rule)] mb-8">
        <FileText className="w-6 h-6 mt-1" strokeWidth={1.25} />
        <div className="min-w-0">
          <p className="font-medium truncate" data-testid="doc-filename">{doc.filename}</p>
          <p className="font-mono-d text-xs text-[var(--ink-3)] mt-1">{doc.page_count} page{doc.page_count === 1 ? "" : "s"}</p>
        </div>
      </div>
      <p className="eyebrow mb-3">We think this is</p>
      <div className="flex flex-wrap items-baseline gap-4 mb-2">
        <h3 className="font-serif-d text-4xl" data-testid="detected-type">{TYPE_LABELS[doc.detected_type]}</h3>
        <span className="font-mono-d text-xs px-2 py-1 bg-[var(--paper-2)]" data-testid="detected-confidence">
          {Math.round(doc.confidence * 100)}% confident
        </span>
      </div>
      {doc.reasoning && <p className="text-sm text-[var(--ink-2)] mb-8 max-w-xl">{doc.reasoning}</p>}
      <p className="eyebrow mb-3">Choose a use case</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 mb-8">
        {playbooks.map((p) => (
          <button key={p.slug} onClick={() => setType(p.slug)} data-testid={`select-type-${p.slug}`}
            className={`text-left px-4 py-3 border text-sm transition-colors duration-150 ${type === p.slug ? "border-[var(--ink)] bg-[var(--ink)] text-[var(--paper)]" : "border-[var(--rule)] hover:border-[var(--ink)]"}`}>
            <span className="font-mono-d text-[10px] opacity-60 mr-2">{p.number}</span>{p.name}
          </button>
        ))}
      </div>
      {needs && (
        <div className="mb-8">
          <p className="eyebrow mb-3">Job description (required)</p>
          <Textarea value={context} onChange={(e) => setContext(e.target.value)} rows={7} data-testid="context-textarea"
            placeholder="Paste the job description you want to compare against..." className="bg-[var(--paper)] rounded-none" />
        </div>
      )}
      <button disabled={!type || (needs && !context.trim())} onClick={onRun} data-testid="run-review-btn"
        className="btn-ink px-7 py-3.5 text-sm font-medium disabled:opacity-40 disabled:pointer-events-none">
        Run {TYPE_LABELS[type] || "review"}
      </button>
    </div>
  );
};

export default function NewReview() {
  const { state } = useLocation();
  const nav = useNavigate();
  const [stage, setStage] = useState("upload");
  const [doc, setDoc] = useState(null);
  const [type, setType] = useState("");
  const [context, setContext] = useState("");
  const [playbooks, setPlaybooks] = useState([]);
  const started = useRef(false);

  useEffect(() => { api.get("/playbooks").then((r) => setPlaybooks(r.data)); }, []);

  const handle = async (req) => {
    setStage("classifying");
    try {
      const { data } = await req();
      setDoc(data);
      setType(data.detected_type === "other" ? "" : data.detected_type);
      setStage("confirm");
    } catch (e) {
      toast.error(errMsg(e));
      setStage("upload");
    }
  };

  const upload = (file) => {
    const fd = new FormData();
    fd.append("file", file);
    handle(() => api.post("/documents/upload", fd));
  };
  const sample = () => handle(() => api.post("/documents/sample"));

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    if (state?.file) upload(state.file);
    else if (state?.sample) sample();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const run = async () => {
    setStage("reviewing");
    try {
      const { data } = await api.post("/reviews", { document_id: doc.id, doc_type: type, context: context || null });
      nav(`/reviews/${data.id}`);
    } catch (e) {
      toast.error(errMsg(e));
      setStage("confirm");
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 pt-16" data-testid="new-review-page">
      <p className="eyebrow mb-4">New review / step {stage === "upload" || stage === "classifying" ? "1" : stage === "confirm" ? "2" : "3"} of 3</p>
      <h1 className="font-serif-d text-4xl sm:text-5xl tracking-tight mb-12">
        {stage === "confirm" ? "Confirm the document type." : stage === "reviewing" ? "Working through the checklist." : "Upload a document."}
      </h1>
      {stage === "upload" && (
        <>
          <Dropzone onFile={upload} compact />
          <button onClick={sample} data-testid="use-sample-btn" className="mt-4 text-sm underline underline-offset-4 decoration-[var(--ink-3)]">
            No file handy? Use a sample NDA
          </button>
        </>
      )}
      {stage === "classifying" && <Working title="Reading the document" note="Pulling out the text and working out the document type." />}
      {stage === "confirm" && doc && <Confirm {...{ doc, type, setType, context, setContext, playbooks, onRun: run }} />}
      {stage === "reviewing" && <Working title={`Running ${TYPE_LABELS[type]}`} note="This usually takes 30 to 90 seconds. Please keep this tab open." />}
      {stage === "reviewing" && <p className="mt-4 flex items-center gap-2 font-mono-d text-xs text-[var(--ink-3)]"><Loader2 className="w-3 h-3 animate-spin" /> Matching quotes to pages</p>}
    </div>
  );
}
